<template>
	<div
		v-if="hasDefaultSlot"
		:class="[
			`badge badge--${color}`,
			{
				'badge--ellipsis': ellipsis,
			},
		]"
		data-cy="badge"
		:data-anchor="hasTooltip ? `badge-tooltip--${_uid}`: null"
		:title="hasTooltip ? null : getSlotText"
		@mouseover="showTooltip = true"
		@mouseleave="showTooltip = false"
	>
		<slot />

		<helio-tooltip
			:active="hasTooltip && showTooltip"
			:anchor="`badge-tooltip--${_uid}`"
			offset="center"
			position="top"
			role="tooltip"
			:skin="tooltipSkin"
		>
			<template v-slot:content>
				{{ getSlotText }}
			</template>
		</helio-tooltip>
	</div>
</template>

<script>
import Tooltip from '../tooltip/Tooltip.vue';

export default {
	name: 'Badge',

	components: {
		'helio-tooltip': Tooltip,
	},

	props: {
		color: {
			type: String,
			required: false,
			default: 'primary',
			validator: value => [
				'primary',
				'accent-2',
				'accent-3',
				'accent-4',
				'accent-5',
				'accent-6',
				'accent-7',
				'accent-8',
				'accent-9',
				'grey',
				'success',
				'alert',
				'danger',
				'info',
			].indexOf(value) !== -1,
			description: 'Sets the background and text colors of the Badge.',
		},

		ellipsis: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Badge should be cut short if the content is too long',
		},

		tooltip: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a tooltip will be displayed on hover. An HTML title is used if disabled.',
		},

		tooltipSkin: {
			type: String,
			required: false,
			default: 'dark',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets the tooltip\'s color skin.',
		},
	},

	data() {
		return {
			showTooltip: false,
		};
	},

	slots: {
		default: {
			required: true,
			scoped: false,
			description: 'Supplies the contents of the Badge',
		},
	},

	computed: {
		getSlotText() {
			if (
				this.$slots.default
				&& this.$slots.default[0]
				&& this.$slots.default[0].text
			) {
				return this.$slots.default[0].text.trim();
			}

			if (
				this.$scopedSlots.default
				&& this.$scopedSlots.default[0]
				&& this.$scopedSlots.default[0].text
			) {
				return this.$scopedSlots.default[0].text.trim();
			}

			return false;
		},

		hasDefaultSlot() {
			return !!this.$slots.default || !!this.$scopedSlots.default;
		},

		hasTooltip() {
			return this.tooltip && this.getSlotText;
		},
	},
};
</script>

<style lang="scss" src="./badge.scss" />
